<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import {
  getVaultAncestors,
  searchVault,
  type ContractManagementContractInDbSlim,
  type VaultFileInDb,
  type VaultFileInDbSlim,
} from '@/api';
import { mapVaultFileToVaultItem } from '@/utils/vaultItemMapping';
import { fetchWholeDirectory, itemsOfKind } from '@/services/vaultListingService';
import { ContractsService } from '@/services/contractManagement/contractsService';
import { useVaultStore } from '@/stores/vaultStore';
import type { VaultBrowserItem, VaultBrowserItemState } from './vaultFileBrowser';
import { useVaultWebSocket } from '@/composables/useVaultWebSocket';
import { ContractFilesService } from '@/services/contractManagement/contractFilesService';

import Breadcrumb, { type BreadcrumbItem } from '@/components/Breadcrumb.vue';
import UserInitialsAvatar from '@/components/UserInitialsAvatar.vue';
import RevisionSignatureCell from '@/components/file_vault/RevisionSignatureCell.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCheckbox from '@/components/design/base/GaiaCheckbox.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import { gaiaAccent, gaiaNeutral, gaiaShadow } from '@/components/design/themes/tokens';
import {
  IconChevronForward,
  IconDocument,
  IconDocumentLock,
  IconEye,
  IconFolder,
} from '@/components/design/icons';

// --- Types ---

interface Props {
  /** v-model: array of 'file-{uid}' / 'dir-{uid}' keys */
  selectedKeys: string[];
  /** Disable all interactions in the browser */
  disabled?: boolean;
  /** When true, only one item can be selected; folder clicks always navigate */
  singleSelection?: boolean;
  /** When true, only files can be selected; folder clicks always navigate */
  fileOnlySelection?: boolean;
  /** When true, show a preview action for file rows */
  showPreviewAction?: boolean;
  /** Allowed file extensions */
  allowedFileTypes?: string[];
  /** Tooltip message for invalid file types */
  fileTypeErrorMsg?: string;
  /** Minimum allowed page count when page metadata is available */
  minPages?: number;
  /** Tooltip message for files below the minimum page count */
  minPagesErrorMsg?: string;
  /** Maximum allowed page count when page metadata is available */
  maxPages?: number;
  /** Tooltip message for files above the maximum page count */
  maxPagesErrorMsg?: string;
  /** Maximum allowed file size in bytes */
  maxFileSizeB?: number;
  /** Tooltip message for files above the max size */
  maxFileSizeBErrorMsg?: string;
  /** Optional per-item selection state override */
  getItemSelectionState?: VaultBrowserItemState;
  /**
   * What a contract is here. `hide` (default): contracts don't appear, as
   * before they existed. `open`: a contract appears in folders and search and
   * opens like a folder onto its files, which are picked one by one (their
   * keys are plain `file-<uid>`). `select`: as `open`, and the contract row
   * itself has a checkbox too (key `contract-<uid>`) — what ticking it means
   * is the caller's, through `toggle`.
   */
  contracts?: 'hide' | 'open' | 'select';
  /**
   * The caller owns the selection: a click only emits `toggle`, and the
   * ticks always mirror `selectedKeys`. For callers whose picks carry rules
   * of their own (the signing step).
   */
  controlled?: boolean;
  /** Start inside this contract, under its folder trail, instead of at the root. */
  startInContractUid?: string | null;
}

type VaultItem = VaultBrowserItem;

interface BreadcrumbNode {
  uid: string | null;
  label: string;
  /** A contract opened like a folder; anything else is a folder (null uid: the root). */
  kind?: 'directory' | 'contract';
}

// --- Props / Emits ---

const vaultStore = useVaultStore();

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  singleSelection: false,
  fileOnlySelection: false,
  showPreviewAction: false,
  allowedFileTypes: () => [],
  fileTypeErrorMsg: '',
  minPages: undefined,
  minPagesErrorMsg: '',
  maxPages: undefined,
  maxPagesErrorMsg: '',
  maxFileSizeB: undefined,
  maxFileSizeBErrorMsg: '',
  getItemSelectionState: undefined,
  contracts: 'hide',
  controlled: false,
  startInContractUid: null,
});

const emit = defineEmits<{
  (e: 'update:selectedKeys', keys: string[]): void;
  (e: 'preview', vaultFileUid: string): void;
  /** A row's checkbox was clicked; `select` is whether it should now be picked. */
  (e: 'toggle', item: VaultItem, select: boolean): void;
}>();

// --- State ---

const searchQuery = ref('');
const isSearchActive = computed(() => searchQuery.value.trim().length > 0);

// Browse state
const items = ref<VaultItem[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);
const isNavigating = ref(false);
const breadcrumbs = ref<BreadcrumbNode[]>([]);
const currentDirUid = ref<string | null>(null);
// Browse renders incrementally so large folders don't mount thousands of rows
// up front. The full directory is loaded into `items`; we only render a growing
// slice and reveal more as the user scrolls (mirrors the search infinite scroll).
const BROWSE_PAGE_SIZE = 50;
const browseVisibleCount = ref(BROWSE_PAGE_SIZE);

// Search / infinite scroll state
const nextOffset = ref(0);
const searchTotal = ref(0);
const isLoadingMore = ref(false);
const isSearchFinished = ref(false);
const searchDebounceHandle = ref<number | null>(null);
const hadActiveSearch = ref(false);
const skipNextSearchReload = ref(false);

// Selection
const selectedSet = ref<Set<string>>(new Set());

const showInitialLoading = computed(() => loading.value && items.value.length === 0);

// During search, `items` is already paged in from the server. During browse,
// the whole folder is loaded but only a slice is rendered.
const visibleItems = computed<VaultItem[]>(() =>
  isSearchActive.value ? items.value : items.value.slice(0, browseVisibleCount.value)
);

// --- Selection Logic ---

watch(
  () => props.selectedKeys,
  keys => {
    // Only keys a row could carry a tick for: with file-only selection that is
    // files, plus contracts when their rows are pickable (`select`).
    const incoming = new Set(
      props.fileOnlySelection
        ? keys.filter(
            key =>
              key.startsWith('file-') ||
              (props.contracts === 'select' && key.startsWith('contract-'))
          )
        : keys
    );
    if (
      incoming.size === selectedSet.value.size &&
      [...incoming].every(key => selectedSet.value.has(key))
    )
      return;
    selectedSet.value = incoming;
  },
  { immediate: true }
);

function emitSelection(): void {
  emit('update:selectedKeys', Array.from(selectedSet.value));
}

function getResolvedItemSelectionState(item: VaultItem): {
  disabled?: boolean;
  reason?: string | null;
} {
  const customState = props.getItemSelectionState?.(item);
  if (customState?.disabled) {
    return customState;
  }

  const builtInReason = getBuiltInValidationReason(item);
  if (builtInReason) {
    return {
      disabled: true,
      reason: builtInReason,
    };
  }

  return customState ?? {};
}

function isItemSelectionDisabled(item: VaultItem): boolean {
  if (props.disabled) return true;
  return getResolvedItemSelectionState(item).disabled === true;
}

function getItemDisabledReason(item: VaultItem): string | null {
  const reason = getResolvedItemSelectionState(item).reason;
  return typeof reason === 'string' && reason.trim().length > 0 ? reason : null;
}

/**
 * Whether a row has a checkbox: files always; folders unless only files may
 * be picked; contracts only when their rows are pickable (`select`).
 */
function isTickable(item: VaultItem): boolean {
  if (item.type === 'contract') return props.contracts === 'select';
  return !props.fileOnlySelection || item.type === 'file';
}

function toggleItem(item: VaultItem): void {
  if (isItemSelectionDisabled(item)) {
    return;
  }

  if (!isTickable(item)) {
    return;
  }

  const select = !selectedSet.value.has(item.key);
  emit('toggle', item, select);
  if (props.controlled) return;

  const newSet = new Set(selectedSet.value);
  if (props.singleSelection) {
    if (newSet.has(item.key)) {
      newSet.clear();
    } else {
      newSet.clear();
      newSet.add(item.key);
    }
  } else {
    if (newSet.has(item.key)) {
      newSet.delete(item.key);
    } else {
      newSet.add(item.key);
    }
  }
  selectedSet.value = newSet;
  emitSelection();
}

function isItemSelected(key: string): boolean {
  return selectedSet.value.has(key);
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Field derivation is shared with the main vault list via the store; see
// `src/utils/vaultItemMapping.ts`. We keep the picker's own flat `VaultItem`
// shape and pick the fields it needs from the shared superset.
function mapVaultFileToItem(file: VaultFileInDbSlim): VaultItem {
  const m = mapVaultFileToVaultItem(file);
  return {
    vaultFile: file,
    key: m.key,
    type: 'file',
    uid: m.uid,
    name: m.name,
    previewStatus: m.previewStatus,
    sizeBytes: m.sizeBytes,
    pageCount: m.pageCount,
    fileExtension: m.fileExtension,
    hasPreviewFile: m.hasPreviewFile,
    ownerName: m.ownerName,
    modifiedTime: m.modifiedTime,
    signatureInfos: m.signatureInfos,
  };
}

function mapContractToItem(contract: ContractManagementContractInDbSlim): VaultItem {
  return {
    key: `contract-${contract.uid}`,
    type: 'contract',
    uid: contract.uid,
    name: contract.name,
    ownerName: contract.owner_user
      ? contract.owner_user.full_name || contract.owner_user.email
      : null,
    modifiedTime: contract.updated_at ?? contract.created_at,
  };
}

function normalizeAllowedFileType(value: string): string {
  return value.trim().replace(/^\./, '').toLowerCase();
}

function getBuiltInValidationReason(item: VaultItem): string | null {
  if (item.type !== 'file') {
    return null;
  }

  const allowedExtensions = props.allowedFileTypes
    .map(normalizeAllowedFileType)
    .filter(value => value.length > 0);

  const fileExtension = item.fileExtension?.toLowerCase() ?? null;
  if (
    allowedExtensions.length > 0 &&
    (!fileExtension || !allowedExtensions.includes(fileExtension))
  ) {
    const allowedDisplay = allowedExtensions.map(extension => `.${extension}`).join(', ');
    const extensionDisplay = fileExtension ? `.${fileExtension}` : 'unknown';
    const allowedSuffix = allowedDisplay ? ` Allowed extensions: ${allowedDisplay}.` : '';

    return (
      props.fileTypeErrorMsg || `File extension ${extensionDisplay} is not allowed.${allowedSuffix}`
    );
  }

  if (
    typeof props.maxFileSizeB === 'number' &&
    typeof item.sizeBytes === 'number' &&
    item.sizeBytes > props.maxFileSizeB
  ) {
    return (
      props.maxFileSizeBErrorMsg ||
      `File size ${formatFileSize(item.sizeBytes)} exceeds the maximum allowed size of ${formatFileSize(props.maxFileSizeB)}.`
    );
  }

  if (
    typeof props.minPages === 'number' &&
    typeof item.pageCount === 'number' &&
    item.pageCount < props.minPages
  ) {
    return (
      props.minPagesErrorMsg ||
      `File has ${item.pageCount} page${item.pageCount === 1 ? '' : 's'}, below the minimum of ${props.minPages}.`
    );
  }

  if (
    typeof props.maxPages === 'number' &&
    typeof item.pageCount === 'number' &&
    item.pageCount > props.maxPages
  ) {
    return (
      props.maxPagesErrorMsg ||
      `File has ${item.pageCount} page${item.pageCount === 1 ? '' : 's'}, exceeding the maximum of ${props.maxPages}.`
    );
  }

  return null;
}

// --- API ---

async function loadDirectory(uid: string | null, mode: 'initial' | 'navigation'): Promise<void> {
  isNavigating.value = mode === 'navigation';
  loading.value = true;
  error.value = null;

  try {
    // The whole folder, all pages: the browser reveals rows as it scrolls.
    const directory = await fetchWholeDirectory(uid ?? undefined);

    currentDirUid.value = directory.uid;

    // Grouped as a picker reads best: folders, then contracts, then files.
    const mapped: VaultItem[] = [];
    for (const dir of itemsOfKind(directory.items, 'directory')) {
      mapped.push({
        key: `dir-${dir.uid}`,
        type: 'directory',
        uid: dir.uid,
        name: dir.directory_name,
      });
    }
    if (props.contracts !== 'hide') {
      for (const contract of itemsOfKind(directory.items, 'contract')) {
        mapped.push(mapContractToItem(contract));
      }
    }
    for (const file of itemsOfKind(directory.items, 'file')) {
      mapped.push(mapVaultFileToItem(file));
    }

    items.value = mapped;
    browseVisibleCount.value = BROWSE_PAGE_SIZE;
  } catch {
    error.value = 'Failed to load directory.';
  } finally {
    loading.value = false;
    isNavigating.value = false;
  }
}

/** A contract's files, as a folder's contents: the contract opened like a folder. */
async function loadContract(uid: string, mode: 'initial' | 'navigation'): Promise<void> {
  isNavigating.value = mode === 'navigation';
  loading.value = true;
  error.value = null;

  try {
    const contractFiles = await ContractFilesService.getAllContractFilesForContract(uid);
    currentDirUid.value = null;
    const contract = { uid, name: breadcrumbs.value.at(-1)?.label ?? '' };
    items.value = contractFiles.flatMap(contractFile =>
      contractFile.vault_file
        ? [{ ...mapVaultFileToItem(contractFile.vault_file), contractFile, contract }]
        : []
    );
    browseVisibleCount.value = BROWSE_PAGE_SIZE;
  } catch {
    error.value = 'Failed to load the contract.';
  } finally {
    loading.value = false;
    isNavigating.value = false;
  }
}

/** Load whatever the last crumb is: a folder, a contract, or the root. */
async function loadCrumb(
  node: BreadcrumbNode | undefined,
  mode: 'initial' | 'navigation'
): Promise<void> {
  if (node?.kind === 'contract' && node.uid) {
    await loadContract(node.uid, mode);
    return;
  }
  await loadDirectory(node?.uid ?? null, mode);
}

async function loadSearchResults(reset: boolean): Promise<void> {
  if (!isSearchActive.value) return;

  if (reset) {
    loading.value = true;
    isSearchFinished.value = false;
    nextOffset.value = 0;
    items.value = [];
    searchTotal.value = 0;
  } else {
    isLoadingMore.value = true;
  }

  error.value = null;

  try {
    const res = await searchVault({
      body: { text: searchQuery.value, limit: 50, offset: reset ? 0 : nextOffset.value },
    });
    if (!res.data) throw new Error('No data');

    const newItems: VaultItem[] = [];
    for (const file of res.data.file_results) {
      newItems.push(mapVaultFileToItem(file));
    }
    // A contract matches on its own fields or any of its files' names.
    if (props.contracts !== 'hide') {
      for (const contract of res.data.contract_results) {
        newItems.push(mapContractToItem(contract));
      }
    }
    for (const dir of res.data.directory_results) {
      newItems.push({
        key: `dir-${dir.uid}`,
        type: 'directory',
        uid: dir.uid,
        name: dir.directory_name,
      });
    }

    items.value = reset ? newItems : [...items.value, ...newItems];
    searchTotal.value =
      res.data.file_results_count +
      res.data.directory_results_count +
      (props.contracts === 'hide' ? 0 : res.data.contract_results_count);
    nextOffset.value = items.value.length;
    isSearchFinished.value = items.value.length >= searchTotal.value;
  } catch {
    error.value = 'Failed to search vault.';
  } finally {
    loading.value = false;
    isLoadingMore.value = false;
  }
}

// --- Navigation ---

/** Open a folder, or a contract like a folder. */
async function navigateToFolder(item: VaultItem): Promise<void> {
  if (item.type === 'file') return;

  // If coming from search, reset breadcrumbs to root first
  if (isSearchActive.value) {
    prepareNavigationFromSearch();
  }

  const node: BreadcrumbNode = { uid: item.uid, label: item.name, kind: item.type };
  breadcrumbs.value.push(node);
  await loadCrumb(node, 'navigation');
}

async function navigateToBreadcrumb(index: number): Promise<void> {
  const target = breadcrumbs.value.at(index);
  if (!target) return;
  breadcrumbs.value = breadcrumbs.value.slice(0, index + 1);
  await loadCrumb(target, 'navigation');
}

function prepareNavigationFromSearch(): void {
  if (searchDebounceHandle.value) {
    window.clearTimeout(searchDebounceHandle.value);
    searchDebounceHandle.value = null;
  }
  breadcrumbs.value = [{ uid: null, label: 'My Vault' }];
  hadActiveSearch.value = false;
  skipNextSearchReload.value = true;
  searchQuery.value = '';
}

// --- Row interaction ---

async function handleItemClick(item: VaultItem): Promise<void> {
  if (isItemSelectionDisabled(item)) {
    return;
  }

  // A contract opens, unless its row can be ticked (`select`), where a click
  // ticks it like a folder in multi-select and the arrow opens it.
  if (item.type === 'contract' && props.contracts !== 'select') {
    await navigateToFolder(item);
    return;
  }

  // In single-selection mode OR when folder is clicked: navigate (folders can't be "selected")
  if (item.type === 'directory' && (props.singleSelection || props.fileOnlySelection)) {
    await navigateToFolder(item);
    return;
  }
  // In multi-select mode: single click = select/deselect
  // Folders are also selectable in multi-select mode
  toggleItem(item);
}

async function handleItemDoubleClick(item: VaultItem): Promise<void> {
  if (isItemSelectionDisabled(item)) return;
  if (item.type === 'file') return;
  await navigateToFolder(item);
}

// --- Breadcrumb items ---

const breadcrumbItems = computed((): BreadcrumbItem[] =>
  breadcrumbs.value.map((crumb, index) => ({
    label: crumb.label,
    clickable: index < breadcrumbs.value.length - 1,
    onClick:
      index < breadcrumbs.value.length - 1
        ? (): void => {
            void navigateToBreadcrumb(index);
          }
        : null,
  }))
);

// --- Infinite scroll ---

async function handleInfiniteLoad(): Promise<void> {
  if (loading.value) return;

  if (isSearchActive.value) {
    if (isSearchFinished.value || isLoadingMore.value) return;
    await loadSearchResults(false);
    return;
  }

  // Browse: reveal the next slice of the already-loaded directory.
  if (browseVisibleCount.value >= items.value.length) return;
  browseVisibleCount.value = Math.min(
    browseVisibleCount.value + BROWSE_PAGE_SIZE,
    items.value.length
  );
}

// --- Search watch ---

watch(
  () => searchQuery.value,
  value => {
    if (skipNextSearchReload.value) {
      skipNextSearchReload.value = false;
      return;
    }

    const trimmed = value.trim();
    const hadSearch = hadActiveSearch.value;
    hadActiveSearch.value = trimmed.length > 0;

    if (!trimmed && hadSearch) {
      // Search cleared: reload the current browse directory
      void loadCrumb(breadcrumbs.value.at(-1), 'initial');
      return;
    }

    if (!trimmed) return;

    if (searchDebounceHandle.value) {
      window.clearTimeout(searchDebounceHandle.value);
    }

    searchDebounceHandle.value = window.setTimeout(() => {
      void loadSearchResults(true);
    }, 300);
  }
);

// --- Helpers ---

function formatModifiedTime(value?: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function handleRetry(): void {
  if (isSearchActive.value) {
    void loadSearchResults(true);
  } else {
    void loadCrumb(breadcrumbs.value.at(-1), 'initial');
  }
}

function handlePreviewClick(item: VaultItem, event: MouseEvent): void {
  event.stopPropagation();
  if (item.type !== 'file' || isItemSelectionDisabled(item)) return;
  emit('preview', item.uid);
}

// With file-only selection a ticked contract only stands for its files, which
// carry keys of their own — counting both read one file as "2 selected".
const selectedCount = computed(() =>
  props.fileOnlySelection
    ? [...selectedSet.value].filter(key => key.startsWith('file-')).length
    : selectedSet.value.size
);

// --- Expose ---

function getAllSelectedUids(): { fileUids: string[]; dirUids: string[]; gaiaUids: string[] } {
  const keys = Array.from(selectedSet.value);
  return {
    fileUids: keys.filter(k => k.startsWith('file-')).map(k => k.replace('file-', '')),
    dirUids: keys.filter(k => k.startsWith('dir-')).map(k => k.replace('dir-', '')),
    gaiaUids: [],
  };
}

async function refreshCurrentDirectory(): Promise<void> {
  if (isSearchActive.value) {
    await loadSearchResults(true);
    return;
  }

  await loadCrumb(breadcrumbs.value.at(-1), 'initial');
}

function getCurrentDirectoryContext(): { uid: string | null; label: string } {
  const currentBreadcrumb = breadcrumbs.value.at(-1);

  return {
    uid: currentDirUid.value,
    label: currentBreadcrumb?.label ?? 'My Vault',
  };
}

function getCurrentBreadcrumbs(): BreadcrumbNode[] {
  return breadcrumbs.value.map(node => ({ uid: node.uid, label: node.label }));
}

/**
 * Navigate into a directory the caller knows about but this list may not show
 * yet — a folder just created from outside the browser (the drafting wizard's
 * destination picker). Same path as clicking the row: a search is dropped
 * first, the crumb is pushed, the directory loads.
 */
async function openDirectory(uid: string, label: string): Promise<void> {
  if (isSearchActive.value) {
    prepareNavigationFromSearch();
  }
  breadcrumbs.value.push({ uid, label });
  await loadDirectory(uid, 'navigation');
}

defineExpose({
  getAllSelectedUids,
  refreshCurrentDirectory,
  getCurrentDirectoryContext,
  getCurrentBreadcrumbs,
  openDirectory,
});

// --- Init ---

/**
 * Open straight onto a contract: the trail is its readable folders (the root
 * by uid, as "My Vault") and then the contract. Falls back to the root.
 */
async function startInContract(uid: string): Promise<void> {
  try {
    const [ancestors, rootUid, contract] = await Promise.all([
      getVaultAncestors({ query: { kind: 'contract', uid }, throwOnError: true }),
      vaultStore.resolveRootDirectoryUid(),
      ContractsService.getById(uid),
    ]);
    const folders = ancestors.data
      .filter(ancestor => ancestor.kind === 'directory' && ancestor.uid !== rootUid)
      .map(ancestor => ({ uid: ancestor.uid, label: ancestor.name, kind: 'directory' as const }));
    breadcrumbs.value = [
      { uid: null, label: 'My Vault' },
      ...folders,
      { uid, label: contract.name, kind: 'contract' },
    ];
    await loadContract(uid, 'initial');
  } catch {
    breadcrumbs.value = [{ uid: null, label: 'My Vault' }];
    await loadDirectory(null, 'initial');
  }
}

onMounted(() => {
  if (props.startInContractUid && props.contracts !== 'hide') {
    void startInContract(props.startInContractUid);
    return;
  }
  breadcrumbs.value = [{ uid: null, label: 'My Vault' }];
  void loadDirectory(null, 'initial');
});

// --- Live vault updates over the websocket ---

useVaultWebSocket({
  directoryUids: () => {
    const uids = breadcrumbs.value
      .filter(node => node.kind !== 'contract')
      .map(node => node.uid)
      .filter((uid): uid is string => uid !== null);
    if (currentDirUid.value) {
      uids.push(currentDirUid.value);
    }
    return Array.from(new Set(uids));
  },
  currentDirectoryUid: () => currentDirUid.value,
  fileUids: () =>
    isSearchActive.value
      ? items.value.filter(item => item.type === 'file').map(item => item.uid)
      : [],
  onCurrentDirectoryUpdated: () => {
    void loadCrumb(breadcrumbs.value.at(-1), 'initial');
  },
  onParentDirectoryUpdated: (uid, name) => {
    const node = breadcrumbs.value.find(entry => entry.uid === uid);
    if (node) {
      node.label = name;
    }
  },
  onCurrentOrParentDeleted: () => {
    breadcrumbs.value = [{ uid: null, label: 'My Vault' }];
    void loadDirectory(null, 'navigation');
  },
  onFileUpdated: (uid, fileData) => {
    const index = items.value.findIndex(item => item.key === `file-${uid}`);
    if (index !== -1) {
      items.value.splice(index, 1, mapVaultFileToItem(fileData as VaultFileInDb));
    }
  },
  onFileDeleted: uid => {
    items.value = items.value.filter(item => item.key !== `file-${uid}`);
  },
});
const ITEM_ICON = { directory: IconFolder, contract: IconDocumentLock, file: IconDocument };

const accentColor = gaiaAccent.base;
const surface = gaiaNeutral.surface;
const menuShadow = gaiaShadow.menu;
const accentSurface = gaiaAccent.wash;
const accentBorder = gaiaAccent.border;
const subtleColor = gaiaNeutral.textSubtle;
const faintColor = gaiaNeutral.textFaint;
const borderColor = gaiaNeutral.borderCard;
const hoverSurface = gaiaNeutral.surfaceSoft;
</script>

<template>
  <div class="vault-browser-content">
    <!-- Header: breadcrumb + search -->
    <div class="browser-toolbar">
      <div class="breadcrumb-wrap">
        <template v-if="isSearchActive">
          <div class="search-results-label">Search results</div>
        </template>
        <template v-else>
          <Breadcrumb :items="breadcrumbItems" :max-visible-items="5" />
        </template>
      </div>
      <div v-if="$slots['toolbar-actions']" class="toolbar-actions">
        <slot name="toolbar-actions" />
      </div>
      <gaia-search-input v-model:value="searchQuery" variant="panel" class="search-input" />
    </div>

    <!-- List area -->
    <div class="list-wrap">
      <!-- Initial loading skeletons -->
      <template v-if="showInitialLoading">
        <div v-for="i in 7" :key="i" class="skeleton-row">
          <NSkeleton circle height="24px" width="24px" />
          <NSkeleton text :repeat="1" style="width: 40%" />
        </div>
      </template>

      <!-- Error state -->
      <template v-else-if="error">
        <div class="empty-state-wrap">
          <gaia-empty-state :title="error" status="alert">
            <gaia-button size="small" @click="handleRetry">Retry</gaia-button>
          </gaia-empty-state>
        </div>
      </template>

      <!-- Empty state -->
      <template v-else-if="items.length === 0 && !loading">
        <div class="empty-state-wrap">
          <gaia-empty-state :title="isSearchActive ? 'No results found' : 'This folder is empty'" />
        </div>
      </template>

      <!-- Item list -->
      <template v-else>
        <NInfiniteScroll
          class="items-scroll"
          :distance="12"
          :finished="isSearchActive ? isSearchFinished : browseVisibleCount >= items.length"
          @load="handleInfiniteLoad"
        >
          <gaia-tooltip
            v-for="item in visibleItems"
            :key="item.key"
            placement="top"
            :disabled="!getItemDisabledReason(item)"
          >
            <template #trigger>
              <div class="item-row-tooltip-trigger">
                <button
                  type="button"
                  class="item-row"
                  :data-item-type="item.type"
                  :data-tour="item.type === 'file' ? 'vault.file-row' : undefined"
                  :data-selected="isItemSelected(item.key)"
                  :disabled="isNavigating || isItemSelectionDisabled(item)"
                  :class="{
                    'item-row--selected': isItemSelected(item.key),
                    'item-row--disabled': isItemSelectionDisabled(item),
                  }"
                  @click="handleItemClick(item)"
                  @dblclick="handleItemDoubleClick(item)"
                >
                  <div class="item-row-main">
                    <!-- Checkbox: shown in multi-select mode for all items; in single mode only for files -->
                    <gaia-checkbox
                      v-if="!singleSelection && isTickable(item)"
                      :checked="isItemSelected(item.key)"
                      :disabled="isItemSelectionDisabled(item)"
                      @update:checked="() => toggleItem(item)"
                      @click.stop
                    />
                    <NIcon
                      :size="18"
                      :color="item.type === 'directory' ? accentColor : subtleColor"
                    >
                      <component :is="ITEM_ICON[item.type]" />
                    </NIcon>
                    <div class="item-content">
                      <gaia-text
                        size="row"
                        weight="medium"
                        :tone="isNavigating || isItemSelectionDisabled(item) ? 'faint' : 'ink'"
                        class="item-name"
                      >
                        {{ item.name }}
                      </gaia-text>
                    </div>
                  </div>

                  <div class="item-row-trailing">
                    <RevisionSignatureCell
                      v-if="item.type === 'file' && item.signatureInfos?.length"
                      :signature-infos="item.signatureInfos"
                      compact
                    />
                    <div
                      v-if="item.ownerName || formatModifiedTime(item.modifiedTime)"
                      class="item-meta-row"
                    >
                      <UserInitialsAvatar
                        v-if="item.ownerName"
                        :user="{ full_name: item.ownerName, email: '' }"
                        size="xs"
                        :show-tooltip="true"
                      />
                      <span
                        v-if="item.ownerName && formatModifiedTime(item.modifiedTime)"
                        class="meta-separator"
                        >•</span
                      >
                      <gaia-text
                        v-if="formatModifiedTime(item.modifiedTime)"
                        size="micro"
                        tone="subtle"
                      >
                        {{ formatModifiedTime(item.modifiedTime) }}
                      </gaia-text>
                    </div>

                    <gaia-icon-button
                      v-if="props.showPreviewAction && item.type === 'file'"
                      :dimension="28"
                      circle
                      class="preview-button"
                      :disabled="isNavigating || isItemSelectionDisabled(item)"
                      @click="handlePreviewClick(item, $event)"
                    >
                      <n-icon :size="16" :color="accentColor">
                        <IconEye />
                      </n-icon>
                    </gaia-icon-button>

                    <gaia-icon-button
                      v-if="item.type !== 'file'"
                      :dimension="28"
                      circle
                      class="folder-arrow-button"
                      :disabled="isNavigating || props.disabled"
                      @click.stop="navigateToFolder(item)"
                    >
                      <n-icon :size="16" :color="faintColor">
                        <IconChevronForward />
                      </n-icon>
                    </gaia-icon-button>
                    <div v-else class="folder-arrow-placeholder" aria-hidden="true" />
                  </div>
                </button>
              </div>
            </template>
            {{ getItemDisabledReason(item) }}
          </gaia-tooltip>
        </NInfiniteScroll>

        <!-- Inline load-more skeletons (search) -->
        <div v-if="isLoadingMore" class="inline-loading">
          <div v-for="i in 3" :key="`more-${i}`" class="inline-loading-row">
            <NSkeleton circle height="18px" width="18px" />
            <NSkeleton text :repeat="1" style="width: 44%" />
          </div>
        </div>

        <!-- Navigation overlay -->
        <div v-if="isNavigating" class="navigation-loading-overlay">
          <div class="navigation-loading-card">
            <span class="loading-spinner" />
            <gaia-text size="row" weight="semibold">Loading…</gaia-text>
          </div>
        </div>
      </template>
    </div>

    <!-- Footer -->
    <div class="browser-footer">
      <gaia-text v-if="selectedCount > 0" size="meta" tone="subtle">
        {{ selectedCount }} selected
      </gaia-text>
    </div>
  </div>
</template>

<style scoped>
.vault-browser-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 200px;
  height: 100%;
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

/* --- Toolbar --- */
.browser-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-shrink: 0;
  min-width: 0;
  overflow: hidden;
}

.breadcrumb-wrap {
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  padding: 0;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.search-results-label {
  font-size: 13px;
  color: v-bind(subtleColor);
  font-weight: 600;
  padding: 4px 0;
}

.search-input {
  width: 200px;
  max-width: 200px;
  flex: 0 1 200px;
}

/* --- List wrap --- */
.list-wrap {
  flex: 1;
  min-height: 0;
  position: relative;
  border: 1px solid v-bind(borderColor);
  border-radius: 12px;
  padding: 8px;
  background: v-bind(surface);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* --- Skeleton rows --- */
.skeleton-row {
  height: 52px;
  display: grid;
  grid-template-columns: 28px 1fr;
  align-items: center;
  gap: 10px;
  padding: 0 10px;
}

/* --- Items scroll --- */
.items-scroll {
  flex: 1;
  overflow-y: auto;
}

/* --- Item row --- */
.item-row {
  width: 100%;
  border: none;
  background: transparent;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 10px;
  padding: 10px;
  transition: all 0.2s;
  cursor: pointer;
  text-align: left;
}

.item-row-tooltip-trigger {
  width: 100%;
}

.item-row:hover {
  background: v-bind(accentSurface);
}

.item-row--selected {
  background: v-bind(accentBorder);
}

.item-row--disabled {
  opacity: 0.5;
}

.item-row--disabled:hover {
  background: v-bind(hoverSurface);
}

.item-row:disabled {
  cursor: not-allowed;
}

.item-row-main {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
}

.item-content {
  display: flex;
  flex: 1;
  min-width: 0;
}

.item-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.item-row-trailing {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-left: auto;
  flex-shrink: 0;
  padding-left: 12px;
}

.item-meta-row {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.meta-separator {
  color: v-bind(faintColor);
}

.folder-arrow-button,
.preview-button,
.folder-arrow-placeholder {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
}

/* --- Inline load-more --- */
.inline-loading {
  border-top: 1px solid v-bind(borderColor);
  margin-top: 6px;
  padding-top: 8px;
}

.inline-loading-row {
  height: 42px;
  display: grid;
  grid-template-columns: 24px 1fr;
  gap: 10px;
  align-items: center;
  padding: 0 10px;
}

/* --- Empty state --- */
/* Fill the list area rather than imposing a fixed floor: `.list-wrap` clips overflow, so a floor
   taller than the host's available height (e.g. the playbook wizard's 240px tab pane) cut the
   centered icon and description off. */
.empty-state-wrap {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* --- Navigation overlay (same as GDrive modal) --- */
.navigation-loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(248, 250, 252, 0.56);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: all;
}

.navigation-loading-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: v-bind(surface);
  border-radius: 10px;
  border: 1px solid v-bind(accentBorder);
  box-shadow: v-bind(menuShadow);
}

.loading-spinner {
  width: 14px;
  height: 14px;
  border-radius: 999px;
  border: 2px solid v-bind(borderColor);
  border-top-color: v-bind(accentColor);
  animation: spin 0.85s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* --- Footer --- */
.browser-footer {
  flex-shrink: 0;
  min-height: 18px;
}
</style>
